'use client';

import { useEffect } from 'react';

/**
 * ThemeInjector — applique les CSS variables du theme au runtime client.
 *
 * IMPORTANT : Les valeurs PAR DEFAUT (orange Ligobox + neutres) sont injectees
 * en SSR via <style> dans app/layout.jsx → presentes des le 1er paint, pas de
 * "mélange de couleurs" sur les blocs CTA/boutons. Ce composant ici sert
 * uniquement a APPLIQUER les overrides admin (theme.color_primary custom,
 * etc.) APRES hydratation, quand le user a edite ses reglages.
 *
 * Pourquoi pas un <style> SSR direct ici : ce composant est importe par des
 * Client Components (HomePageContent / CmsPageContent). Rendre <style> ici
 * causait une hydration mismatch (#418 / #423) qui faisait crasher Chromium
 * en headless. La regle Next.js : les <style> doivent venir de Server
 * Components pour rester stable entre SSR et hydratation.
 */
export default function ThemeInjector({ settings }) {
  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    const theme = settings.theme || {};

    // Couleurs
    const setColor = (cssVar, value) => {
      if (value) root.style.setProperty(cssVar, value);
    };
    setColor('--color-primary',       theme.color_primary    || settings.primary_color);
    setColor('--color-primary-dark',  theme.color_primary_dark);
    setColor('--color-secondary',     theme.color_secondary  || settings.secondary_color);
    setColor('--color-accent',        theme.color_accent);
    setColor('--color-bg',            theme.color_bg);
    setColor('--color-bg-alt',        theme.color_bg_alt);
    setColor('--color-surface',       theme.color_surface);
    setColor('--color-text',          theme.color_text);
    setColor('--color-text-muted',    theme.color_text_muted);
    setColor('--color-border',        theme.color_border);
    setColor('--hero-bg-from',        theme.hero_bg_from);
    setColor('--hero-bg-to',          theme.hero_bg_to);

    const primary = theme.color_primary || settings.primary_color;
    if (primary) {
      root.style.setProperty('--color-primary-text', getContrastTextColor(primary));
    }

    if (theme.font_family_heading) root.style.setProperty('--font-heading', theme.font_family_heading);
    if (theme.font_family_body)    root.style.setProperty('--font-body',    theme.font_family_body);
    if (theme.font_weight_heading) root.style.setProperty('--font-weight-heading', String(theme.font_weight_heading));
    if (theme.font_weight_body)    root.style.setProperty('--font-weight-body',    String(theme.font_weight_body));
    if (theme.base_font_size)      root.style.setProperty('--font-size-base', `${theme.base_font_size}px`);

    if (theme.radius_global  != null) root.style.setProperty('--radius-global',  `${theme.radius_global}px`);
    if (theme.radius_buttons != null) root.style.setProperty('--radius-buttons', `${theme.radius_buttons}px`);
    if (theme.radius_cards   != null) root.style.setProperty('--radius-cards',   `${theme.radius_cards}px`);

    const density = theme.spacing_density || 'normal';
    const sectionPy = density === 'compact' ? '70px' : density === 'spacious' ? '140px' : '100px';
    root.style.setProperty('--section-py', sectionPy);

    // ── Header bg : variable CSS dediee, mise a jour instantanee
    // Quand admin change la couleur header, settings re-fetch ramene la
    // nouvelle valeur, ce useEffect re-tourne et update --ligobox-header-bg.
    // Le navigateur repaint le header sans re-render React → pas de flash.
    const header = settings.header || {};
    const hbg = header.bg_color || '#ffffff';
    const hopacity = Math.max(0, Math.min(100,
      Number.isFinite(+header.bg_opacity) ? +header.bg_opacity : 95)) / 100;
    const hAsRgba = hexToRgbaSafe(hbg, hopacity);
    root.style.setProperty('--ligobox-header-bg', hAsRgba);
  }, [settings]);

  return null;
}

// Helper : convertit "#991414" + 0.95 → "rgba(153,20,20,0.95)"
function hexToRgbaSafe(hex, alpha) {
  if (!hex || typeof hex !== 'string') return `rgba(255,255,255,${alpha})`;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function getContrastTextColor(hexColor) {
  if (!hexColor) return '#ffffff';
  const hex = hexColor.replace('#', '');
  if (hex.length !== 6) return '#ffffff';
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#000000' : '#ffffff';
}
